// client/src/components/MonthlyReview/MonthlyReviewPage.js
// F15 — Monthly Review Wrap-Up Wizard
// 4-step MUI Stepper: Month in Numbers → Budget Performance → Insights → Next Month Plan

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle,
  TrendingUp,
  TrendingDown,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';
import { useInsightsFilter } from '../Insights/shared/InsightsFilterContext';
import InsightsDateFilter from '../Insights/shared/InsightsDateFilter';

// ── Constants ──────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const STEPS = [
  'Month in Numbers',
  'Budget Performance',
  'Insights',
  'Next Month Plan',
];

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon, subtitle }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
          {icon && (
            <Box sx={{ color, opacity: 0.8 }}>
              {icon}
            </Box>
          )}
        </Box>
        <Typography
          variant="h5"
          sx={{ mt: 1, fontWeight: 700, color: color || 'text.primary' }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ── Step 1 — Month in Numbers ──────────────────────────────────────────────────
function StepMonthInNumbers({ data }) {
  const theme = useTheme();
  const { totalIncome, totalExpenses, netSavings, savingsRate, dailyCumulativeSpend, ageOfMoney } = data;

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  return (
    <Box>
      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Income"
            value={formatCurrency(totalIncome)}
            color={theme.palette.success.main}
            icon={<ArrowUpward fontSize="small" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Expenses"
            value={formatCurrency(totalExpenses)}
            color={theme.palette.error.main}
            icon={<ArrowDownward fontSize="small" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Net Savings"
            value={formatCurrency(netSavings)}
            color={netSavings >= 0 ? theme.palette.success.main : theme.palette.error.main}
            icon={netSavings >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
            subtitle={netSavings < 0 ? 'Spent more than earned' : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Savings Rate"
            value={`${savingsRate.toFixed(1)}%`}
            color={
              savingsRate >= 20
                ? theme.palette.success.main
                : savingsRate >= 10
                ? theme.palette.warning.main
                : theme.palette.error.main
            }
            subtitle={`Age of Money: ${ageOfMoney} day${ageOfMoney !== 1 ? 's' : ''}`}
          />
        </Grid>
      </Grid>

      {/* Daily Cumulative Spend AreaChart */}
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Cumulative Spending — Day by Day
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={dailyCumulativeSpend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="cumulGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}`}
                interval={4}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `€${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                width={55}
              />
              <Tooltip
                formatter={(v) => [`€${v.toFixed(2)}`, 'Cumulative Spend']}
                labelFormatter={(l) => `Day ${l}`}
              />
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                fill="url(#cumulGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Step 2 — Budget Performance ────────────────────────────────────────────────
function StepBudgetPerformance({ data }) {
  const theme = useTheme();
  const { budgetAdherence, topCategories } = data;
  const { onTrack, overBudget, underFunded, details = [] } = budgetAdherence;

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  const statusChip = (status) => {
    if (status === 'over-budget')
      return <Chip label="Over" size="small" color="error" variant="outlined" />;
    if (status === 'under-funded')
      return <Chip label="Unset" size="small" color="default" variant="outlined" />;
    return <Chip label="On Track" size="small" color="success" variant="outlined" />;
  };

  const total = onTrack + overBudget + underFunded;

  return (
    <Box>
      {/* Summary Banner */}
      <Alert
        severity={overBudget > 0 ? 'warning' : 'success'}
        icon={false}
        sx={{ mb: 2, borderRadius: 2 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {onTrack} on track{overBudget > 0 ? ` · ${overBudget} over budget` : ''}{underFunded > 0 ? ` · ${underFunded} unset` : ''}
          {total > 0 ? ` (${total} total budget${total !== 1 ? 's' : ''})` : ''}
        </Typography>
      </Alert>

      {/* Budget Table */}
      {details.length > 0 ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 3 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' } }}>
                <TableCell>Category</TableCell>
                <TableCell align="right">Budgeted</TableCell>
                <TableCell align="right">Spent</TableCell>
                <TableCell align="right">Diff</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.map((row) => (
                <TableRow key={row.name} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                  <TableCell align="right">{formatCurrency(row.budgeted)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.spent)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: row.difference >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                  >
                    {row.difference >= 0 ? '+' : ''}{formatCurrency(row.difference)}
                  </TableCell>
                  <TableCell align="center">{statusChip(row.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          No active budgets found for this month. Set up budgets to track your spending limits.
        </Alert>
      )}

      {/* Top 5 Categories BarChart */}
      {topCategories.length > 0 && (
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Top Spending Categories
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={topCategories.slice(0, 5)}
                layout="vertical"
                margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `€${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={90}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip formatter={(v) => [`€${v.toFixed(2)}`, 'Spent']} />
                <Bar dataKey="amount" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ── Step 3 — Insights ─────────────────────────────────────────────────────────
function StepInsights({ data }) {
  const { highlights, unusualSpend } = data;

  return (
    <Box>
      {/* Highlights */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Month Highlights
      </Typography>
      {highlights.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Not enough data for highlights yet. Add more transactions!
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {highlights.map((h, i) => (
            <Alert key={i} severity="info" sx={{ borderRadius: 2 }}>
              {h}
            </Alert>
          ))}
        </Box>
      )}

      {/* Unusual Spend */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Unusual Spending Detected
      </Typography>
      {unusualSpend.length === 0 ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          No unusual spending detected — your spending patterns look consistent!
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {unusualSpend.map((u, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {u.category}
                    </Typography>
                    <Chip
                      label={`${u.spike.toFixed(1)}×`}
                      size="small"
                      color="warning"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                  <Alert severity="warning" icon={false} sx={{ py: 0.5, borderRadius: 1 }}>
                    <Typography variant="caption">
                      Spent <strong>€{u.amount.toFixed(2)}</strong> vs avg <strong>€{u.avgAmount.toFixed(2)}</strong>
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// ── Step 4 — Next Month Plan ───────────────────────────────────────────────────
function StepNextMonthPlan({ data, onClose }) {
  const { nextMonthSuggestions, goalsProgress } = data;
  const theme = useTheme();

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  return (
    <Box>
      {/* Suggestions */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Actionable Suggestions
      </Typography>
      <Card
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 3 }}
      >
        <List disablePadding>
          {nextMonthSuggestions.map((s, i) => (
            <React.Fragment key={i}>
              <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">{s}</Typography>
                  }
                />
              </ListItem>
              {i < nextMonthSuggestions.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Card>

      {/* Goals Progress */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Savings Goals Progress
      </Typography>
      {goalsProgress.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          No active savings goals found. Create goals to track your financial targets!
        </Alert>
      ) : (
        <Card
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 3 }}
        >
          <CardContent>
            {goalsProgress.map((g, i) => (
              <Box key={i} sx={{ mb: i < goalsProgress.length - 1 ? 2.5 : 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {g.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(g.currentAmount)} / {formatCurrency(g.targetAmount)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={g.progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': { borderRadius: 4 },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {g.progress}% reached
                  </Typography>
                  {g.deposited > 0 && (
                    <Typography variant="caption" color="success.main">
                      +{formatCurrency(g.deposited)} this month
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Close Review button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<CheckCircle />}
          onClick={onClose}
          sx={{ borderRadius: 2, px: 4 }}
        >
          Close Review
        </Button>
      </Box>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MonthlyReviewPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const now = new Date();
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth(); // 1-indexed, previous month
  const defaultYear  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedYear, setSelectedYear]   = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [reviewData, setReviewData]       = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);

  // Sync month/year selectors with the global Insights date filter
  const { to: filterTo } = useInsightsFilter();
  useEffect(() => {
    const d = new Date(filterTo);
    if (!isNaN(d.getTime())) {
      setSelectedMonth(d.getMonth() + 1); // 1-indexed
      setSelectedYear(d.getFullYear());
    }
  }, [filterTo]);

  // Build year options: current year and 2 years back
  const yearOptions = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  const fetchReview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get('/monthly-review', {
        params: { year: selectedYear, month: selectedMonth },
      });
      setReviewData(resp.data.data);
      setActiveStep(0);
    } catch (err) {
      console.error('[MonthlyReview] fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load monthly review. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleClose = () => {
    const key = `sk_lastReview_${selectedYear}_${String(selectedMonth).padStart(2, '0')}`;
    localStorage.setItem('sk_lastReview', `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`);
    localStorage.setItem(key, new Date().toISOString());
    // Navigate back or show confirmation
    window.history.back();
  };

  const stepContent = reviewData
    ? [
        <StepMonthInNumbers data={reviewData} key="s1" />,
        <StepBudgetPerformance data={reviewData} key="s2" />,
        <StepInsights data={reviewData} key="s3" />,
        <StepNextMonthPlan data={reviewData} onClose={handleClose} key="s4" />,
      ]
    : [];

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          📋 Monthly Review
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your comprehensive financial wrap-up
        </Typography>
      </Box>

      {/* Global Insights date-range filter */}
      <InsightsDateFilter />

      {/* Month / Year Selector */}
      <Card
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2, mb: 3 }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <MenuItem key={idx} value={idx + 1}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              onClick={fetchReview}
              disabled={loading}
              size="small"
            >
              {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Load Review
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Stepper Wizard */}
      {!loading && reviewData && (
        <>
          {/* Period label */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Reviewing: <strong>{reviewData.period.label}</strong>
          </Typography>

          {/* Horizontal Stepper (collapsed to icons on mobile) */}
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 3 }}
          >
            {STEPS.map((label, index) => (
              <Step key={label} completed={index < activeStep}>
                <StepLabel
                  onClick={() => setActiveStep(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="caption" sx={{ fontWeight: activeStep === index ? 700 : 400 }}>
                    {label}
                  </Typography>
                </StepLabel>
                {/* Mobile: render step content inline */}
                {isMobile && (
                  <StepContent>
                    <Box sx={{ py: 1 }}>
                      {stepContent[index]}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      {index > 0 && (
                        <Button size="small" onClick={handleBack} variant="outlined">
                          Back
                        </Button>
                      )}
                      {index < STEPS.length - 1 && (
                        <Button size="small" onClick={handleNext} variant="contained">
                          Continue
                        </Button>
                      )}
                    </Box>
                  </StepContent>
                )}
              </Step>
            ))}
          </Stepper>

          {/* Desktop: render active step content below stepper */}
          {!isMobile && (
            <>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: { xs: 2, sm: 3 },
                  minHeight: 320,
                }}
              >
                {stepContent[activeStep]}
              </Card>

              {/* Navigation */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  sx={{ borderRadius: 2 }}
                >
                  Back
                </Button>
                {activeStep < STEPS.length - 1 && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ borderRadius: 2 }}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Container>
  );
}
